import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

let globalLoginMessage = null;

/**
 * Emmagatzema globalment un missatge perquè es pugui llegir des de
 * la pàgina de login (útil després d'un tancament de sessió per timeout).
 * 
 * @param {string} msg - El missatge a mostrar.
 */
export const setGlobalLoginMessage = (msg) => {
  globalLoginMessage = msg;
};

/**
 * Retorna el missatge global i el neteja perquè només es mostri un cop.
 * 
 * @returns {string|null} El missatge o null.
 */
export const consumeGlobalLoginMessage = () => {
  const msg = globalLoginMessage;
  globalLoginMessage = null;
  return msg;
};

import localforage from 'localforage';

/**
 * Neteja les dades emmagatzemades al dispositiu relacionades amb els xats.
 */
function clearAllChatStorage() {
  localforage.removeItem('chat_list').catch(() => {});
}

/**
 * Proveïdor de l'estat d'autenticació per a l'aplicació.
 * Gestiona les dades de sessió com l'usuari actual, el rol, el 2FA, 
 * i permet el login/logout netejant l'estat.
 * 
 * @param {Object} props - L'aplicació continguda (children).
 */
export function AuthProvider({ children }) {
  const [isAuth, setIsAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [pfpUrl, setPfpUrl] = useState(null);

  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch('/api/me', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setIsAuth(true);
        setUsername(data.username);
        setFirstName(data.first_name);
        setUserId(String(data.userId));
        setRole(data.role || 'user');
        setTwoFactorEnabled(!!data.two_factor_enabled);
        setPfpUrl(data.pfp_url || null);
      } else {
        clearSessionState();
      }
    } catch {
      clearSessionState();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const login = (newToken, newUsername, newUserId, newRole = 'user', newPfpUrl = null, has2FA = false, newFirstName = null) => {
    clearAllChatStorage();
    // newToken is ignored as it is handled via cookies
    setUsername(newUsername);
    setFirstName(newFirstName);
    setUserId(String(newUserId));
    setRole(newRole);
    setTwoFactorEnabled(has2FA);
    setPfpUrl(newPfpUrl);
    setIsAuth(true);
  };

  const clearSessionState = () => {
    setUsername(null);
    setFirstName(null);
    setUserId(null);
    setRole(null);
    setTwoFactorEnabled(false);
    setPfpUrl(null);
    setIsAuth(false);
  };

  const logout = async (message = null) => {
    globalLoginMessage = message;

    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch { }

    clearAllChatStorage();

    clearSessionState();
  };

  const refreshSession = () => fetchSession();

  if (isLoading) {
    return (
      <div className="global-spinner-container full-screen">
        <div className="global-spinner"></div>
        <div>Carregant sessió...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ username, firstName, userId, role, twoFactorEnabled, pfpUrl, setPfpUrl, login, logout, refreshSession, isAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook personalitzat per accedir fàcilment al context d'autenticació.
 * 
 * @returns {Object} Dades i funcions d'autenticació (isAuth, username, role, etc.).
 */
export const useAuth = () => useContext(AuthContext);
